/**
 * Speed bake (W1-04 core-ir; DESIGN.md §6.11, rpe-format.md F§7.2, F§7.4).
 *
 * In Classic (legacy) speed mode Phira ignores the easing of speed events and integrates every event as a linear
 * ramp. An eased speed segment of the model is therefore exported as linear pieces:
 *  - nodes at dyadic subdivisions of the segment in beats, `t0 + (t1 - t0) * j / 2^n` (exact rationals, so every
 *    piece boundary is an exact RPE triple);
 *  - every piece lasts at least 1 ms (the legacy integrator samples `b - 1e-4` s inside each piece);
 *  - each piece STARTS at the exact speed at its start node and ENDS at the value that makes the floor distance
 *    Phira integrates over the piece equal to the exact integral of the model curve (adaptive Gauss-Legendre
 *    quadrature), so the floor has zero drift at every node AS PHIRA PLAYS IT. Phira's legacy integrator does not
 *    take the piece's end value but samples the ramp 0.1 ms before the end, `e = S(b - 1e-4)`, and integrates the
 *    ramp from `s` to that `e` (F§7.2 step 4-5); for a ramp `vs -> ve` over `h` seconds that loses
 *    `(ve - vs) * 0.5e-4` speed-seconds per piece, which would add up over a long bake. The written end value is
 *    therefore `vs + (e* - vs) * h / (h - 1e-4)`, where `e* = 2 * integral / h - vs` is the end the sampled ramp
 *    must reach (other layers' keyframes and zero crossings split the piece in Phira, but the losses of the parts
 *    add up to the same amount because the ramp is linear across the piece);
 *  - n grows until the CUMULATIVE floor-position error max |H_played(t) - H_exact(t)| over the whole segment, with
 *    H_played the floor Phira computes (the ramp `vs -> e*` inside each piece), is at most `speedBakeTolPx` RPE-y px
 *    (or until a finer level would break the 1 ms / exact-triple limits).
 * Progress is measured in seconds, as Phira does, so a segment spanning a BPM change bakes correctly.
 * The rpe170 integrator integrates a linear event exactly (`(s + e) / 2` per second, F§7.3) and samples nothing
 * before the end, so a segment baked for it (a bezier on speed, which no mode may write; the model invariants
 * forbid it, lowering stays defensive) passes `endSampleSec: 0` and every piece simply ends at `e*`.
 * The returned error is in RPE-y px: speed units x seconds x SPEED_RATIO x 450.
 */

import { Ease } from '../ease/index';
import type { Ease as EaseValue } from '../ease/types';
import { RPE_Y_PER_HEIGHT, SPEED_RATIO } from '../model/units';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import type { NonEmpty } from '../util/types';

/** Shortest piece Phira's legacy integrator handles without sampling before the piece (F§7.2): 1 ms. */
export const MIN_PIECE_SEC = 1e-3;
/** Phira's legacy integrator samples the speed this many seconds before the end of each keyframe pair (F§7.2). */
export const PHIRA_END_SAMPLE_SEC = 1e-4;
/** Deepest dyadic level tried (2^16 pieces); the 1 ms limit normally stops much earlier. */
export const MAX_BAKE_LEVEL = 16;
/** RPE-y px of floor position per (speed unit x second). */
export const PX_PER_SPEED_SECOND = SPEED_RATIO * RPE_Y_PER_HEIGHT;

export interface BakePiece {
  readonly startBeat: Beat;
  readonly endBeat: Beat;
  /** Exact speed at the start node. */
  readonly start: number;
  /**
   * Integral-matching end value: Phira's legacy integrator, which samples the ramp 0.1 ms before the piece ends,
   * integrates exactly the model's floor distance over the piece (see `writtenEnd`).
   */
  readonly end: number;
}

export interface BakeResult {
  readonly pieces: NonEmpty<BakePiece>;
  /**
   * Cumulative max |H_played - H_exact| over the segment in RPE-y px, H_played being the floor position Phira's
   * legacy integrator computes from the pieces (0.1 ms end sample included); +Infinity when the model curve is not finite
   * (a degenerate slice: Phira's clamped tween divides by zero), in which case one linear piece is written, which is
   * what Phira's legacy integrator plays for the unbaked event anyway.
   */
  readonly maxErrorPx: number;
  /** Dyadic level used (pieces = 2^level). */
  readonly level: number;
}

export interface BakeInput {
  readonly t0: Beat;
  readonly t1: Beat;
  /** Seconds of the written start / end triples (Phira's keyframe times). */
  readonly sec0: number;
  readonly sec1: number;
  /** Segment start value (key vOut) and end value (next key vIn). */
  readonly v0: number;
  readonly v1: number;
  readonly ease: EaseValue;
  readonly tolPx: number;
  /** Beat -> chart seconds (Phira op order). */
  readonly secAt: (b: Beat) => number;
  /**
   * How long before each piece's end the target integrator samples the speed: `PHIRA_END_SAMPLE_SEC` for the legacy
   * integrator (the default), 0 for the rpe170 integrator (exact trapezoid per linear event).
   */
  readonly endSampleSec?: number;
}

// 5-point Gauss-Legendre on [-1, 1].
const GL_X = [0, -0.5384693101056831, 0.5384693101056831, -0.906179845938664, 0.906179845938664] as const;
const GL_W = [
  0.5688888888888889, 0.47862867049936647, 0.47862867049936647, 0.23692688505618908, 0.23692688505618908,
] as const;

/** Integral of f over [a, b] by 5-point Gauss-Legendre. */
export function gaussLegendre5(f: (x: number) => number, a: number, b: number): number {
  const half = (b - a) / 2;
  const mid = (a + b) / 2;
  let s = 0;
  for (let i = 0; i < 5; i++) s += (GL_W[i] ?? 0) * f(mid + half * (GL_X[i] ?? 0));
  return s * half;
}

/** Absolute integration tolerance (speed units x seconds): about 1e-7 RPE-y px of floor position. */
const QUAD_TOL = 1e-7 / (SPEED_RATIO * RPE_Y_PER_HEIGHT);
const QUAD_MAX_DEPTH = 8;
/**
 * Relative noise floor of the integrand: Phira's tweens are f32 (about 6e-8 relative), so two quadratures of the
 * same interval cannot agree better than that; refining below it would only chase rounding noise.
 */
const F32_NOISE = 1e-6;

function adaptive(
  f: (x: number) => number,
  a: number,
  b: number,
  whole: number,
  tol: number,
  scale: number,
  depth: number,
  prevDiff: number,
): number {
  const m = (a + b) / 2;
  const left = gaussLegendre5(f, a, m);
  const right = gaussLegendre5(f, m, b);
  const sum = left + right;
  const diff = Math.abs(sum - whole);
  const limit = Math.max(tol, F32_NOISE * scale * (b - a));
  // Stop when converged, at the depth limit, or when halving stopped helping: a smooth piece or a kink improves
  // by 8x or more per halving; rounding noise (a sliced curve amplifies f32 noise by 1 / (f(r) - f(l))) does not.
  if (depth >= QUAD_MAX_DEPTH || !(diff > limit) || diff > prevDiff / 4) return sum;
  return (
    adaptive(f, a, m, left, tol / 2, scale, depth + 1, diff) + adaptive(f, m, b, right, tol / 2, scale, depth + 1, diff)
  );
}

/**
 * Integral of f over [a, b] by adaptive Gauss-Legendre (halving while two levels disagree and the disagreement
 * shrinks), so the kinks of piecewise curves (Bounce, the InOut joint) are integrated as accurately as smooth
 * parts. `scale`: the magnitude of f (for the f32 noise floor).
 */
export function integrate(f: (x: number) => number, a: number, b: number, scale = 1): number {
  if (!(b > a)) return 0;
  return adaptive(f, a, b, gaussLegendre5(f, a, b), QUAD_TOL, Math.abs(scale), 0, Number.POSITIVE_INFINITY);
}

/** Error samples per piece: dense while pieces are long (curve features inside one piece), sparser when fine. */
function samplesPerPiece(count: number): number {
  return count <= 16 ? 32 : count <= 256 ? 16 : 8;
}

interface Level {
  readonly pieces: NonEmpty<BakePiece>;
  readonly maxErrorPx: number;
  readonly finite: boolean;
}

/** Exact dyadic nodes of [t0, t1] at level n, or null when a node is not exactly representable. */
function dyadicNodes(t0: Beat, t1: Beat, n: number): Beat[] | null {
  const count = 2 ** n;
  const span = Beats.sub(t1, t0);
  const report = { rounded: 0 };
  const nodes: Beat[] = [t0];
  for (let j = 1; j < count; j++) {
    const b = Beats.add(t0, Beats.mulRat(span, j, count, report), report);
    const prev = nodes[nodes.length - 1] as Beat;
    if (report.rounded > 0 || !Beats.lt(prev, b)) return null;
    nodes.push(b);
  }
  if (!Beats.lt(nodes[nodes.length - 1] as Beat, t1)) return null;
  nodes.push(t1);
  return nodes;
}

/**
 * The end value to write for a piece of `h` seconds starting at `vs` whose sampled end must be `target` (the end
 * of the linear ramp with the exact integral): Phira's legacy integrator reads `e = vs + (ve - vs) * (h - 1e-4) / h`,
 * so `ve = vs + (target - vs) * h / (h - 1e-4)`. Pieces too short to compensate (a whole segment shorter than
 * 0.2 ms, never a subdivided piece) are written with the target itself, and so is every piece for an integrator
 * that samples nothing before the end (`sample` 0: rpe170).
 */
export function writtenEnd(vs: number, target: number, h: number, sample = PHIRA_END_SAMPLE_SEC): number {
  if (!(sample > 0) || !(h > 2 * sample)) return target;
  return vs + ((target - vs) * h) / (h - sample);
}

/**
 * The end value the integrator samples for a piece written as `vs -> ve` over `h` seconds (`sample` seconds before
 * the end: 0.1 ms for the legacy integrator, 0 for rpe170, which reads `ve` itself).
 */
export function sampledEnd(vs: number, ve: number, h: number, sample = PHIRA_END_SAMPLE_SEC): number {
  if (!(sample > 0) || !(h > sample)) return ve;
  return vs + ((ve - vs) * (h - sample)) / h;
}

function evaluateLevel(input: BakeInput, nodes: readonly Beat[], secs: readonly number[]): Level {
  const { sec0, sec1, v0, v1, ease } = input;
  const sample = input.endSampleSec ?? PHIRA_END_SAMPLE_SEC;
  const dur = sec1 - sec0;
  const dv = v1 - v0;
  const curve = (t: number): number => v0 + dv * Ease.modelY(ease, dur > 0 ? (t - sec0) / dur : 0);
  const count = nodes.length - 1;
  const sub = samplesPerPiece(count);
  const scale = Math.abs(v0) + Math.abs(v1);
  const pieces: BakePiece[] = [];
  let hLin = 0;
  let hEx = 0;
  let maxErr = 0;
  let finite = true;
  const integrals: number[] = new Array<number>(sub).fill(0);
  for (let j = 0; j < count; j++) {
    const a = secs[j] as number;
    const b = secs[j + 1] as number;
    const h = b - a;
    const vs = curve(a);
    let total = 0;
    for (let q = 0; q < sub; q++) {
      const sa = a + (h * q) / sub;
      const sb = q === sub - 1 ? b : a + (h * (q + 1)) / sub;
      const part = integrate(curve, sa, sb, scale);
      integrals[q] = part;
      total += part;
    }
    // `target`: the end of the linear ramp whose integral is the exact one; `ve`: the value written so that Phira's
    // sampled end is `target`; `played`: the end Phira actually samples (equal to `target` up to rounding).
    const target = h > 0 ? (2 * total) / h - vs : vs;
    const ve = writtenEnd(vs, target, h, sample);
    const played = sampledEnd(vs, ve, h, sample);
    if (!Number.isFinite(vs) || !Number.isFinite(ve) || !Number.isFinite(total)) finite = false;
    let ex = hEx;
    for (let q = 0; q < sub; q++) {
      ex += integrals[q] as number;
      const u = q === sub - 1 ? h : (h * (q + 1)) / sub;
      const lin = hLin + vs * u + (h > 0 ? ((played - vs) * u * u) / (2 * h) : 0);
      const err = Math.abs(lin - ex);
      if (err > maxErr) maxErr = err;
    }
    hLin += ((vs + played) / 2) * h;
    hEx += total;
    pieces.push({ startBeat: nodes[j] as Beat, endBeat: nodes[j + 1] as Beat, start: vs, end: ve });
  }
  return { pieces: pieces as unknown as NonEmpty<BakePiece>, maxErrorPx: maxErr * PX_PER_SPEED_SECOND, finite };
}

/** Bakes one eased speed segment into linear pieces (see the module header). */
export function bakeSpeedSegment(input: BakeInput): BakeResult {
  let best: (Level & { readonly level: number }) | null = null;
  for (let level = 0; level <= MAX_BAKE_LEVEL; level++) {
    const nodes = dyadicNodes(input.t0, input.t1, level);
    if (nodes === null) break;
    const secs = nodes.map((b, j) => (j === 0 ? input.sec0 : j === nodes.length - 1 ? input.sec1 : input.secAt(b)));
    if (level > 0) {
      let shortest = Infinity;
      for (let j = 1; j < secs.length; j++)
        shortest = Math.min(shortest, (secs[j] as number) - (secs[j - 1] as number));
      if (!(shortest >= MIN_PIECE_SEC)) break;
    }
    const lv = evaluateLevel(input, nodes, secs);
    if (!lv.finite) {
      return {
        pieces: [{ startBeat: input.t0, endBeat: input.t1, start: input.v0, end: input.v1 }],
        maxErrorPx: Number.POSITIVE_INFINITY,
        level: 0,
      };
    }
    best = { ...lv, level };
    if (lv.maxErrorPx <= input.tolPx) break;
  }
  if (best === null) {
    // Unreachable for a valid segment (level 0 always has exact nodes); keep a linear piece as Phira plays it.
    return {
      pieces: [{ startBeat: input.t0, endBeat: input.t1, start: input.v0, end: input.v1 }],
      maxErrorPx: Number.POSITIVE_INFINITY,
      level: 0,
    };
  }
  return { pieces: best.pieces, maxErrorPx: best.maxErrorPx, level: best.level };
}
